import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

const ChainContext = createContext();
// Create a global placeholder for chain logos to be available immediately after login
export const chainCache = {
  chains: [],
  logoMap: new Map(),
  colorMap: new Map(),
  isHQMap: new Map(),
  loaded: false
};

export function useChains() {
  return useContext(ChainContext);
}

export function ChainProvider({ children }) {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);
  const isInitializedRef = useRef(false);
  const lastActivityRef = useRef(Date.now());
  const checkActivityIntervalRef = useRef(null);

  // Function to check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const auth = getAuth();
    return !!auth.currentUser;
  }, []);

  // Function to check if the app is in active use
  const isActivelyUsed = useCallback(() => {
    const now = Date.now();
    const INACTIVITY_THRESHOLD = 10 * 60 * 1000; // 10 minutes
    return (now - lastActivityRef.current) < INACTIVITY_THRESHOLD;
  }, []);

  // Function to update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Helper function to process chains and update global cache
  const processAndCacheChains = useCallback((chainsList) => {
    // Update the component state
    setChains(chainsList);
    
    // Update the global cache for immediate access
    chainCache.chains = chainsList;
    chainCache.loaded = true;
    
    // Clear and rebuild logo map for fast lookups
    chainCache.logoMap.clear();
    chainCache.colorMap.clear();
    chainCache.isHQMap.clear();
    chainsList.forEach(chain => {
      if (chain.name && chain.logoUrl) {
        chainCache.logoMap.set(chain.name.toLowerCase(), chain.logoUrl);
      }
      if (chain.name && chain.primaryColor) {
        chainCache.colorMap.set(chain.name.toLowerCase(), chain.primaryColor);
      }
      if (chain.name) {
        chainCache.isHQMap.set(chain.name.toLowerCase(), !!chain.isHQ);
      }
    });
    
    // Log cache status
    console.log(`Chain cache updated with ${chainsList.length} chains, ${chainCache.logoMap.size} logos, ${chainCache.colorMap.size} primary colors, and ${chainCache.isHQMap.size} HQ flags`);
  }, []);

  // Set up event listeners to track user activity
  useEffect(() => {
    // Register user activity events
    const activityEvents = ['mousedown', 'keydown', 'mousemove', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      updateActivity();
    };
    
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [updateActivity]);

  // Function to toggle listener based on activity
  const manageListener = useCallback((isActive) => {
    // Skip if not authenticated
    if (!isAuthenticated()) return;
    
    if (!isActive && unsubscribeRef.current) {
      console.log('ChainContext: Suspending real-time listener due to inactivity');
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    } else if (isActive && !unsubscribeRef.current && chains.length === 0) {
      console.log('ChainContext: Resuming listener due to user activity');
      // Reset the initialization flag to force a new fetch
      isInitializedRef.current = false;
    }
  }, [chains.length, isAuthenticated]);

  // Set up periodic check for inactivity
  useEffect(() => {
    checkActivityIntervalRef.current = setInterval(() => {
      manageListener(isActivelyUsed());
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => {
      if (checkActivityIntervalRef.current) {
        clearInterval(checkActivityIntervalRef.current);
      }
    };
  }, [isActivelyUsed, manageListener]);

  // Set up listener for auth state changes to reload chains when user logs in
  useEffect(() => {
    const auth = getAuth();
    
    const authStateChanged = () => {
      // User is now authenticated, initialize chains
      if (auth.currentUser && chains.length === 0) {
        isInitializedRef.current = false;
        updateActivity();
      } 
      // User logged out, clear chains
      else if (!auth.currentUser) {
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        setChains([]);
        isInitializedRef.current = false;
      }
    };
    
    const unsubscribeAuth = auth.onAuthStateChanged(authStateChanged);
    
    return () => {
      unsubscribeAuth();
    };
  }, [chains.length, updateActivity]);

  useEffect(() => {
    // Skip fetching if we're not authenticated or already have chain data
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }
    
    if (isInitializedRef.current && chains.length > 0) {
      return;
    }

    // Update activity timestamp when fetching chains
    updateActivity();

    const fetchChains = async () => {
      try {
        setLoading(true);
        
        // Try the primary query first (with displayOrder)
        try {
          // Make sure we clear any existing listener before setting up a new one
          if (unsubscribeRef.current) {
            unsubscribeRef.current();
            unsubscribeRef.current = null;
          }

          const chainsQuery = query(
            collection(db, 'chains'),
            orderBy('displayOrder', 'asc'), 
            orderBy('name', 'asc')
          );

          const unsub = onSnapshot(
            chainsQuery,
            { includeMetadataChanges: false },
            (snapshot) => {
              const chainsList = [];
              snapshot.forEach((doc) => {
                chainsList.push({
                  id: doc.id,
                  displayOrder: doc.data().displayOrder || 0,
                  ...doc.data(),
                  logoUrl: doc.data().logoUrl || null
                });
              });

              console.log('Chains loaded with displayOrder:', chainsList.length);
              processAndCacheChains(chainsList);
              setLoading(false);
              setError(null);
              isInitializedRef.current = true;
              updateActivity();
            },
            (error) => {
              console.error('Error with displayOrder query:', error);
              // Don't set error here - we'll fall back to the simpler query
              fallbackQuery();
            }
          );
          
          // Store the unsubscribe function
          unsubscribeRef.current = unsub;
          console.log('ChainContext: Real-time listener set up (primary)');
        } catch (error) {
          console.error('Error setting up displayOrder query:', error);
          fallbackQuery();
        }
        
        async function fallbackQuery() {
          console.log('Using fallback query with name ordering only');
          try {
            // Make sure we clear any existing listener before setting up a new one
            if (unsubscribeRef.current) {
              unsubscribeRef.current();
              unsubscribeRef.current = null;
            }
            
            // Fallback to just name ordering if displayOrder index is missing
            const simpleQuery = query(
              collection(db, 'chains'),
              orderBy('name', 'asc')
            );
            
            const unsub = onSnapshot(
              simpleQuery,
              { includeMetadataChanges: false },
              (snapshot) => {
                const chainsList = [];
                snapshot.forEach((doc) => {
                  chainsList.push({
                    id: doc.id,
                    displayOrder: doc.data().displayOrder || 0, // Default to 0 if missing
                    ...doc.data(),
                    logoUrl: doc.data().logoUrl || null
                  });
                });
                
                console.log('Chains loaded with name-only query:', chainsList.length);
                processAndCacheChains(chainsList);
                setLoading(false);
                setError(null);
                isInitializedRef.current = true;
                updateActivity();
              },
              (fallbackError) => {
                console.error('Error with fallback query:', fallbackError);
                setError('Failed to load chains. Please try again later.');
                setLoading(false);
              }
            );
            
            // Store the unsubscribe function
            unsubscribeRef.current = unsub;
            console.log('ChainContext: Real-time listener set up (fallback)');
          } catch (fallbackError) {
            console.error('Error setting up fallback query:', fallbackError);
            setError('Failed to connect to the database. Please try again later.');
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Error in chain loading process:', error);
        setError('Failed to connect to the database. Please try again later.');
        setLoading(false);
      }
    };

    fetchChains();

    // Cleanup function to unsubscribe from Firestore listeners
    return () => {
      if (unsubscribeRef.current) {
        console.log('ChainContext: Unsubscribing from chains listener');
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (checkActivityIntervalRef.current) {
        clearInterval(checkActivityIntervalRef.current);
        checkActivityIntervalRef.current = null;
      }
    };
  }, [chains.length, updateActivity, processAndCacheChains, isAuthenticated]);

  // Helper function to get chain by name
  const getChainByName = useCallback((name) => {
    if (!name) return null;
    
    // Try global cache first (always available, even outside React)
    if (chainCache.loaded && chainCache.chains.length > 0) {
      return chainCache.chains.find(chain => 
        chain.name && chain.name.toLowerCase() === name.toLowerCase()
      ) || null;
    }
    
    // Fall back to context state
    if (!chains) return null;
    return chains.find(chain => chain.name === name) || null;
  }, [chains]);

  // Helper function to get chain logo URL
  const getChainLogoUrl = useCallback((name) => {
    if (!name) return null;
    
    // Try logo map from cache first (fastest)
    if (chainCache.logoMap.has(name.toLowerCase())) {
      return chainCache.logoMap.get(name.toLowerCase());
    }
    
    // Try finding in chains array as fallback
    const chain = chainCache.chains.find(c => 
      c.name && c.name.toLowerCase() === name.toLowerCase()
    ) || chains.find(c => 
      c.name && c.name.toLowerCase() === name.toLowerCase()
    );
    
    // Cache the result for future lookups
    const logoUrl = chain?.logoUrl || null;
    if (logoUrl) {
      chainCache.logoMap.set(name.toLowerCase(), logoUrl);
    }
    
    return logoUrl;
  }, [chains]);

  // Helper function to get chain primary color
  const getChainPrimaryColor = useCallback((name) => {
    if (!name) return null;
    
    // Try color map from cache first (fastest)
    if (chainCache.colorMap.has(name.toLowerCase())) {
      return chainCache.colorMap.get(name.toLowerCase());
    }
    
    // Try finding in chains array as fallback
    const chain = chainCache.chains.find(c => 
      c.name && c.name.toLowerCase() === name.toLowerCase()
    ) || chains.find(c => 
      c.name && c.name.toLowerCase() === name.toLowerCase()
    );
    
    // Cache the result for future lookups
    const primaryColor = chain?.primaryColor || null;
    if (primaryColor) {
      chainCache.colorMap.set(name.toLowerCase(), primaryColor);
    }
    
    return primaryColor;
  }, [chains]);

  // Function to preload logo images after data is loaded
  useEffect(() => {
    if (chains.length > 0 && !loading) {
      chains.forEach(chain => {
        if (chain.logoUrl) {
          // Preload image
          const img = new Image();
          img.src = chain.logoUrl;
        }
      });
    }
  }, [chains, loading]);

  // Helper function to check if a chain is marked as HQ
  const isChainHQ = useCallback((name) => {
    if (!name) return false;
    
    // Try isHQ map from cache first (fastest)
    if (chainCache.isHQMap.has(name.toLowerCase())) {
      return chainCache.isHQMap.get(name.toLowerCase());
    }
    
    // Try finding in chains array as fallback
    const chain = chainCache.chains.find(c => 
      c.name && c.name.toLowerCase() === name.toLowerCase()
    ) || chains.find(c => 
      c.name && c.name.toLowerCase() === name.toLowerCase()
    );
    
    // Cache the result for future lookups
    const isHQ = !!chain?.isHQ;
    chainCache.isHQMap.set(name.toLowerCase(), isHQ);
    
    return isHQ;
  }, [chains]);

  // Memoize context value to prevent unnecessary rerenders
  const value = useMemo(() => ({
    chains,
    loading,
    error,
    getChainByName,
    getChainLogoUrl,
    getChainPrimaryColor,
    isChainHQ
  }), [chains, loading, error, getChainByName, getChainLogoUrl, getChainPrimaryColor, isChainHQ]);

  // Only update the window reference when chains change, not on every render
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.chainContext = value;
    }
  }, [value]);

  return (
    <ChainContext.Provider value={value}>
      {children}
    </ChainContext.Provider>
  );
}

export default ChainProvider; 